/**
 * TeamWorthChart — "worth over time" line plot for the public team view.
 * 2026-07-21 (owner request) — rivals can watch a competitor's net worth
 * arc across gameweeks (net worth is leaderboard-public knowledge).
 * Falls back to the roster market-value series when no gameweek
 * snapshots exist yet for this team.
 */
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { formatFrancAdaptive } from '../lib/utils';

const fmtTick = (v) => (Math.abs(v) >= 10000 ? `₣${(v / 1000).toFixed(1)}k` : formatFrancAdaptive(v));

export default function TeamWorthChart({ netWorthSeries, rosterValueSeries }) {
  // Build 4 — prefer whichever series has MORE resolution. The gameweek
  // net-worth series often has just 1 snapshot + "now" (a flat 2-point
  // line); the roster-value series carries up to 60 daily points, giving
  // team views the same granular multi-week chart as the Stage sparkline.
  const net = netWorthSeries || [];
  const roster = rosterValueSeries || [];
  const useNet = net.length >= 2 && net.length >= roster.length;
  const raw = useNet ? net : roster;
  if (raw.length < 2) return null;
  const data = raw.map((p, i) => ({
    label: p.week != null ? `W${p.week}` : (p.date || '').slice(5, 10).replace('-', '/'),
    value: p.value,
    now: i === raw.length - 1,
  }));
  const up = data[data.length - 1].value >= data[0].value;
  const color = up ? 'hsl(var(--positive-text))' : 'hsl(var(--destructive))';
  return (
    <div className="bg-card border border-border/60 rounded-2xl p-4 mb-4" data-testid="team-worth-chart">
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-sm font-semibold">{useNet ? 'Net worth over time' : 'Roster value over time'}</h3>
        <span className="text-xs text-muted-foreground tabular-nums">
          {formatFrancAdaptive(data[data.length - 1].value)} now
        </span>
      </div>
      <ResponsiveContainer width="100%" height={150}>
        <LineChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: -4 }}>
          <XAxis dataKey="label" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
          <YAxis tick={{ fontSize: 10 }} tickFormatter={fmtTick} tickLine={false} axisLine={false} width={56} domain={['auto', 'auto']} />
          <Tooltip
            formatter={(v) => [formatFrancAdaptive(v), 'Worth']}
            contentStyle={{ fontSize: 12, borderRadius: 8 }}
          />
          <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={{ r: 2 }} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
